import { useState, useCallback, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { IressEditor } from '@iress/ids-editor';
import { AiDisclaimer } from '@/components/AiDisclaimer';

import { SummariseFooter } from '@/components/summarise/SummariseFooter';
import { ProgressStepper } from '@/components/ProgressStepper';
import { ProgressStep } from '@/lib/progress/steps';
import { UploadCard } from '@/components/UploadCard';
import { MeetingWorkspaceLayout } from '@/components/MeetingWorkspaceLayout';
import { TranscriptSlideout } from '@/components/ResponsiveTranscriptPanel';
import { useMeeting } from '@/lib/hooks/useMeeting';
import { useSummary } from '@/lib/hooks/useSummary';
import { useDraftSave } from '@/lib/hooks/useDraftSave';
import { uploadTranscript } from '@/lib/api';
import { meetingQueryOptions } from '@/lib/query/meetingQuery';
import { summaryQueryOptions } from '@/lib/query/summaryQuery';
import type { Summary } from '@meeting-assistant/schemas';

import {
  IressInline,
  IressField,
  IressStack,
  IressText,
  IressPanel,
  IressSkeleton,
  IressAlert,
  IressStyled,
} from '@iress-oss/ids-components';

export const Route = createFileRoute('/$id/summarise')({
  component: MeetingSummarisePage,
});

function SummarySkeleton() {
  return (
    <IressStack gap="spacing.4" py="spacing.4">
      <IressSkeleton mode="text" textStyle="typography.heading.3" width="60%" />
      <IressSkeleton mode="text" width="100%" />
      <IressSkeleton mode="text" width="95%" />
      <IressSkeleton mode="text" width="90%" />
      <IressSkeleton mode="text" textStyle="typography.heading.4" width="50%" />
      <IressSkeleton mode="text" width="100%" />
      <IressSkeleton mode="text" width="80%" />
    </IressStack>
  );
}

/** Rich text editor for the AI-generated file note. */
function EditableMeetingSummary({
  initialValue,
  onChange,
  validationError,
}: Readonly<{
  initialValue: string;
  onChange?: (value: string) => void;
  validationError: string | null;
}>) {
  const [value, setValue] = useState(initialValue);

  const handleChange = useCallback(
    (newValue: string) => {
      setValue(newValue);
      onChange?.(newValue);
    },
    [onChange],
  );

  return (
    <IressStyled
      stretch
      mt="-spacing.1"
      className="file-note-editor"
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <IressField
        label="Meeting summary"
        hiddenLabel
        removeErrorMargin
        error={validationError}
        stretch
        flex="1"
        style={{
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <IressEditor
          aria-label="Meeting summary"
          format="markdown"
          toolbar={<IressEditor.MarkdownToolbar />}
          value={value}
          onChange={handleChange}
          borderRadius="none"
          stretch
          flex="1"
        />
      </IressField>
    </IressStyled>
  );
}

const TEMPLATE_NAME = 'Client Review';

/** Default error message when the API doesn't provide one. */
const DEFAULT_UPLOAD_ERROR = 'Failed to upload transcript. Please try again.';

/** Handles file upload and updates meeting cache. */
function useFileUpload(id: string) {
  const queryClient = useQueryClient();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = useCallback(
    async (file: File) => {
      setUploadError(null);
      setIsUploading(true);
      try {
        const text = await file.text();
        const updated = await uploadTranscript(id, text);
        queryClient.setQueryData(meetingQueryOptions(id).queryKey, (prev) =>
          prev ? { ...prev, transcript: updated.transcript } : updated,
        );
      } catch (error) {
        const message =
          error instanceof Error ? error.message : DEFAULT_UPLOAD_ERROR;
        setUploadError(message);
      } finally {
        setIsUploading(false);
      }
    },
    [id, queryClient],
  );

  return { handleFileSelect, uploadError, isUploading };
}

/** Upload state — shown when no transcript has been uploaded yet. */
function UploadPanel({
  handleFileSelect,
  isUploading,
  uploadError,
}: Readonly<{
  handleFileSelect: (file: File) => void;
  isUploading: boolean;
  uploadError: string | null;
}>) {
  return (
    <IressPanel p="spacing.2" stretch borderRadius="radius.2" flex="1">
      <IressStack gap="spacing.3" mb="spacing.3">
        <UploadCard
          templateName={TEMPLATE_NAME}
          onFileSelect={handleFileSelect}
          isUploading={isUploading}
        />
      </IressStack>
      {uploadError && <IressAlert status="danger">{uploadError}</IressAlert>}
    </IressPanel>
  );
}

/** Editor state — shown when transcript exists and summary is available. */
function EditorPanel({
  summaryData,
  onChange,
  validationError,
}: Readonly<{
  summaryData: { meetingSummary: string } | undefined;
  onChange?: (value: string) => void;
  validationError: string | null;
}>) {
  return (
    <IressStack
      p="none"
      stretch
      borderRadius="none"
      gap="spacing.4"
      flex="1"
      scrollable="x"
    >
      <IressText element="h3" textStyle="typography.heading.4">
        File Note Editor
      </IressText>

      {summaryData ? (
        <>
          <EditableMeetingSummary
            initialValue={summaryData.meetingSummary}
            onChange={onChange}
            validationError={validationError}
          />
        </>
      ) : (
        <SummarySkeleton />
      )}
    </IressStack>
  );
}

/** Left panel content — renders upload or editor based on transcript state. */
function LeftPanelContent({
  hasTranscript,
  summaryData,
  meetingId,
  handleFileSelect,
  isUploading,
  uploadError,
  onNext,
}: Readonly<{
  hasTranscript: boolean;
  summaryData: { meetingSummary: string } | undefined;
  meetingId: string;
  handleFileSelect: (file: File) => void;
  isUploading: boolean;
  uploadError: string | null;
  onNext: () => void;
}>) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { handleChange, saveNow, status, lastSavedAt, validationError } =
    useDraftSave(meetingId, summaryData?.meetingSummary, (savedContent) => {
      queryClient.setQueryData<Summary>(
        summaryQueryOptions(meetingId).queryKey,
        (prev) => (prev ? { ...prev, meetingSummary: savedContent } : prev),
      );
    });

  const [showDraftValidationError, setShowDraftValidationError] =
    useState(false);

  useEffect(() => {
    if (!validationError) setShowDraftValidationError(false);
  }, [validationError]);

  const handleBack = useCallback(() => void navigate({ to: '/' }), [navigate]);
  const handleSave = useCallback(() => {
    if (validationError) setShowDraftValidationError(true);
    saveNow();
  }, [saveNow, validationError]);
  const handleNext = useCallback(() => {
    if (validationError) {
      setShowDraftValidationError(true);
      return;
    }
    onNext();
  }, [onNext, validationError]);

  return (
    <IressStack
      gap="spacing.4"
      style={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ProgressStepper
        step={
          hasTranscript
            ? ProgressStep.MeetingSummary
            : ProgressStep.UploadTranscript
        }
      />

      {hasTranscript ? (
        <EditorPanel
          summaryData={summaryData}
          onChange={handleChange}
          validationError={validationError}
        />
      ) : (
        <UploadPanel
          handleFileSelect={handleFileSelect}
          isUploading={isUploading}
          uploadError={uploadError}
        />
      )}

      {hasTranscript && (
        <IressInline
          gap="spacing.3"
          verticalAlign="middle"
          horizontalAlign="between"
          style={{ flexWrap: 'wrap' }}
        >
          <AiDisclaimer />
        </IressInline>
      )}

      {hasTranscript && summaryData && (
        <SummariseFooter
          status={status}
          lastSavedAt={lastSavedAt}
          onSave={handleSave}
          onBack={handleBack}
          onNext={handleNext}
          validationError={showDraftValidationError ? validationError : null}
        />
      )}
    </IressStack>
  );
}

function MeetingSummarisePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: meeting, isLoading } = useMeeting();
  const { data: summaryData, transcript } = useSummary();
  const { handleFileSelect, uploadError, isUploading } = useFileUpload(id);

  // The file note is saved to XPLAN on the actions page (alongside the
  // selected actions) — here the button just advances to that step.
  const goToActions = useCallback(
    () => void navigate({ to: '/$id/actions', params: { id } }),
    [navigate, id],
  );

  const hasTranscript = !!transcript;
  const clientName = meeting?.client?.main?.name ?? '';
  const meetingDate = meeting?.datetime
    ? new Date(meeting.datetime).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';

  return (
    <MeetingWorkspaceLayout
      clientName={clientName}
      meetingDate={meetingDate}
      transcript={transcript?.content}
      isLoading={isLoading}
      transcriptSlideout={<TranscriptSlideout entries={transcript?.content} />}
    >
      <LeftPanelContent
        hasTranscript={hasTranscript}
        summaryData={summaryData}
        meetingId={meeting?.id ?? ''}
        handleFileSelect={handleFileSelect}
        isUploading={isUploading}
        uploadError={uploadError}
        onNext={goToActions}
      />
    </MeetingWorkspaceLayout>
  );
}
